/**
 * components/AdminConsole.tsx — the frame every `#/admin/*` route is drawn inside.
 *
 * Before this existed, People, Access, Business units and Audit were **flat peers of the work screen**:
 * one `NavItem[]`, one `nav.map`, one flex column with a uniform gap. From the user's side "People" was
 * exactly as much a peer of "Automations" as "Audit" was, and an ordinary user's entire primary navigation
 * was one item. The console folds the four into one destination whose contents dilate with the caller's
 * grants. That is the shape the design review found in both sibling applications, neither of which has
 * a superuser submenu; the standards forbid one independently of what the siblings do.
 *
 * **The strip comes first in the DOM and first visually. No CSS `order`, ever.** `design-deviations.md`
 * already claims that focus order follows visual order at every width, "corrected by moving the block in
 * the document, never with CSS `order`". Placing the strip above the page head in the document keeps the two orders identical with no context provider and no change to any
 * of the four page modules.
 *
 * **Links carrying `aria-current`, explicitly not `role="tablist"`.** `02-patterns.md` §2.4 is direct
 * about it: a strip whose destinations are addresses is navigation, and `role="tablist"` would announce
 * that the content changes in place while the browser is in fact changing page. The browser's Back
 * button must mean what it says.
 *
 * **Presence.** §2.4 requires the strip on *every* route of the module, "its permission-denied and
 * not-found dead ends included" — which is why the scope refusal below is drawn inside the console
 * rather than replacing the body, and why the denied state is a page within the frame. The one case
 * where no strip is drawn is a caller who holds no section at all: there is nothing to enumerate, and a
 * refusal decorated with a rule and a row of nothing is worse than a refusal.
 *
 * **It scrolls; it does not wrap — and it says which way it continues (7 August 2026).** The row was
 * `flex-wrap: wrap` at every width, which on a phone is two or three rows of tabs and, per
 * `03-components.md` §8 and `02-patterns.md` §2.4, a defect twice over: §8's mobile rule forbids the
 * second row outright, and each extra row moves the page head away from the top bar, varying inside one
 * console the one distance §2.1 fixes across the whole app. Making it scroll is not, however, a free
 * substitution — §8.1 binds the moment it does, and a strip is cut between whole pills with its
 * scrollbar suppressed, so it ends tidily and reads as complete while the sections past the cut go
 * undiscovered. `ScrollStrip` carries that half; see its header for the clause, line by line.
 *
 * The semantics here are deliberately untouched by it: still a `<nav>` of anchors carrying
 * `aria-current`, still labelled, still with Overview first. `ScrollStrip` is a scroll container and
 * reads exactly one thing out of what it is given — the `[aria-current]` node, so it can scroll it into
 * view, which is the other half of §8's mobile rule.
 */
import type { ReactNode } from "react";
import type { AdminConsoleSection, AdminSectionSpec } from "../lib/admin";
import type { ScopeSummary } from "../lib/api";
import { ScrollStrip } from "./ScrollStrip";
import { EmptyState, Page, Select } from "./ui";

export interface AdminConsoleProps {
  /** Only the sections this caller holds. Never the whole table — §2.4 forbids enumerating the rest. */
  readonly sections: readonly AdminSectionSpec[];
  /**
   * The section on screen; `null` for the landing page; `undefined` for a console address that
   * resolved to nothing.
   *
   * Three states rather than two, because the strip has three answers. The third is the one that is
   * easy to lose: an in-console not-found is a route OF the module (§2.4 counts a module's dead ends
   * as its routes, which is why the strip renders at all here), but it is not any of the destinations
   * the strip offers — so no item carries `aria-current`. Folding it into `null` marks *Overview* as
   * the current page above a body saying the address matched nothing.
   */
  readonly active: AdminConsoleSection | null | undefined;
  readonly children: ReactNode;
}

export function AdminConsole(props: AdminConsoleProps): JSX.Element {
  return (
    <div className="admin-console">
      {props.sections.length === 0 ? null : (
        // `?? null` only here: `ScrollStrip` wants a change signal to re-scroll the current item into
        // view, and "no current item" is one value to it however it arose. The `aria-current`
        // comparisons below keep the three states apart, which is where the distinction matters.
        <ScrollStrip currentKey={props.active ?? null}>
          <nav className="admin-subnav" aria-label="Administration sections">
            {/* Ticketless has no breadcrumb component, so without this there is no way back to the landing
                page from a section except the browser’s Back button. */}
            <a
              className="admin-subnav__tab"
              href="#/admin"
              aria-current={props.active === null ? "page" : undefined}
            >
              Overview
            </a>
            {props.sections.map((spec) => (
              <a
                key={spec.key}
                className="admin-subnav__tab"
                href={spec.href}
                aria-current={props.active === spec.key ? "page" : undefined}
              >
                {spec.label}
              </a>
            ))}
          </nav>
        </ScrollStrip>
      )}
      {props.children}
    </div>
  );
}

export interface ScopeNeededProps {
  readonly scopes: readonly ScopeSummary[];
  readonly onSelectBu: (buId: string) => void;
  /** Whether to offer the way to create the first unit. Drawn only where the caller holds the section. */
  readonly canCreateBusinessUnit: boolean;
}

/**
 * Audit is the one scoped section, and this is what it shows before a unit is chosen.
 *
 * **The trap this closes.** People, Access and Business units are in `SCOPELESS_ROUTES`; Audit is
 * deliberately not, because its route resolves scope access on the server. Under the old flat
 * arrangement a scoped route with no active unit fell to the shell-level branch that replaces the
 * **entire body** with "Choose a business unit" — which inside a console would strand the
 * administrator: the strip would vanish and the three sections they could have used would go with it.
 *
 * So the frame moves and the mount condition does not. `AdminAudit` is still never mounted without a
 * scope — it would otherwise issue `GET /api/admin/audit` with no `X-Ticketless-BU` header, which is
 * the leak shape the whole scoping arrangement exists to prevent — and `admin-audit` is still absent
 * from `SCOPELESS_ROUTES`, which `CLAUDE.md` §1c forbids adding to.
 *
 * The choice is offered **in place**. Pointing at the switcher in the top bar would be answering a
 * question the reader has already asked.
 *
 * ⚠️ **`scopes.length === 0` measures REACHABILITY, and this screen must not report it as existence.**
 * Until 6 August 2026 the empty branch read *"No business unit exists yet — an audit trail belongs to
 * exactly one business unit, and none has been created"*, which is simply false on any deployment with
 * `REQUIRE_BU_CONFINEMENT` on (the default) where nobody has yet assigned this delegate a unit: the
 * registry holds one, `GET /api/admin/business-units` is not filtered by accessible scope, and the
 * button beneath the sentence therefore led straight to a table listing the unit the screen had just
 * said did not exist. The console cannot know whether a unit exists — it has asked for nobody's
 * registry — so it says only what it does know, which is that the reader can reach none. The
 * shell-level branch this replaced hedged correctly; that hedge is restored here.
 */
export function ScopeNeeded(props: ScopeNeededProps): JSX.Element {
  if (props.scopes.length === 0) {
    return (
      <Page title="Audit">
        <EmptyState
          title="You cannot reach any business unit"
          body={
            props.canCreateBusinessUnit
              ? "An audit trail belongs to exactly one business unit, and you can reach none. Membership is carried by a group, never by your account itself. If this deployment is new, no unit has been created yet — start there."
              : "An audit trail belongs to exactly one business unit, and you can reach none. Membership is carried by a group, never by your account itself — an administrator has to add you to one."
          }
          {...(props.canCreateBusinessUnit
            ? {
                action: (
                  <a className="btn btn--primary" href="#/admin/business-units">
                    <span className="btn__label">Go to business units</span>
                  </a>
                ),
              }
            : {})}
        />
      </Page>
    );
  }

  return (
    <Page title="Audit">
      <EmptyState
        title="Choose a business unit to read its audit trail"
        body="Every entry belongs to exactly one unit, so there is nothing to show until you say which."
        action={
          <Select
            // Not "Business unit": the top bar's switcher already carries that name, and two comboboxes
            // announced identically on one screen is a real defect for anyone tabbing through them.
            label="Business unit to read"
            value=""
            options={[
              { value: "", label: "Choose a business unit" },
              ...props.scopes.map((scope) => ({ value: scope.id, label: scope.displayName })),
            ]}
            onChange={(value) => {
              if (value !== "") props.onSelectBu(value);
            }}
          />
        }
      />
    </Page>
  );
}
